import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { clone, uniqWith } from 'ramda';
import meQuery from '../../../Rutiner/Kalender/Queries/MeQuery/MeQuery.gql';
import { useQuery } from '@apollo/client';
import {
  ResursQuery,
  ResursQueryVariables,
  ResursQuery_db_BOResurs_All,
} from '../../../Rutiner/Kalender/Queries/ResursQuery/_QueryTypes/ResursQuery';
import {
  MeQuery,
  MeQueryVariables,
} from '../../../Rutiner/Kalender/Queries/MeQuery/_QueryTypes/MeQuery';
import resursQuery from '../../../Rutiner/Kalender/Queries/ResursQuery/ResursQuery.gql';
import {
  IResursTyp,
  ResursTypContext,
} from '../../../Contexts/ResursTypContext/ResursTypContext';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { RutinSettingsContext } from '../../../../_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import {
  dateTimeStringIsValidDate,
  getDate,
  tryDateToDateString,
  firstDayInWeek,
  firstDayInMonth,
  incDay,
} from '../../../../../../Utils/dateUtils';
import mergeSort from '../../../../../../Utils/mergeSort';
import { ITreeSection } from '../../../../../../BaseComponents/TreeSelect/TreeSelectUtils';
import { sortBokningsrubriker } from '../../../Rutiner/Kalender/Components/SchedulerComponent/Utils/resourcesSortUtils';
import { bokningsrubrikFromDbResurs } from '../Utils/AktivitetUtils';
import { useIsSmallScreen } from '../../../../../../Hooks/useFullscreenView';
import { IBokningsrubrik } from '../AktivitetResursValjare';
import { ObjektVyKort } from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import { EDAutoCompleteEdit } from '../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDCheckbox from '../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDButton from '../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDGrid from '../../../../../../BaseComponents/GridComponents/EDGrid';
import EDDatePicker from '../../../../../../BaseComponents/DatePicker/EDDatePicker';
import EDChip from '../../../../../../BaseComponents/Chip/EDChip';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import EDDialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import EDTreeSelect from '../../../../../../BaseComponents/TreeSelect/EDTreeSelect';
import {
  RapportSettings,
  useReadRapportSettings,
  useWriteRapportSettings,
} from '../../../../_StandardRapport/StandardRapportSettings';
import { rapportInfos } from '../../../../_Common/Const/RapportInfo';
import { RapportQuery } from '../../../../_StandardRapport/Queries/RapportQuery/RapportQuery';
import {
  booleanToString,
  stringToBoolean,
} from '../../../../../../Utils/Formatters/Utils/ValueUtils';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material/styles';
import { ICollectionDataType } from '../../../../_Common/Components/BusinessCollectionComponents/Queries/BusinessCollectionQuery';
import {
  FinnsAllaAktiviteter,
  FinnsAllaAktiviteterVariables,
} from '../Queries/_QueryTypes/FinnsAllaAktiviteter';
import finnsAllaAktiviteter from '../Queries/FinnsAllaAktiviteterQuery.gql';

interface IProps {
  showRapportDialog: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
  urval: IUrval[];
  dayCount: number;
  currentDate: Date;
  currentView: string;
  sortedData: ICollectionDataType[];
  testId?: string;
}

interface IUrval {
  id: string;
  value: string;
}

const DAY_COLUMNS = 7;
const WEEK_ROWS = 6;

const useStyles = makeStyles()((theme: Theme) => ({
  paper: {
    maxWidth: '670px',
    background: theme.palette.background.default,
  },
}));

export const RapportDialog: React.FC<React.PropsWithChildren<IProps>> = (
  props: IProps
) => {
  const { classes } = useStyles();
  const resursTypContext = useContext(ResursTypContext);
  const resTypes = resursTypContext.resTypes;
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const routineAvailabilityContext = useContext(RutinSettingsContext);
  const isSmallScreen = useIsSmallScreen();
  const routineID = routineAvailabilityContext.activeRoutine?.routine.rutinID;
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [sections, setSections] = useState<ITreeSection<IBokningsrubrik>[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [sorting, setSorting] = useState<string>('Datum');
  const [preliminaryBookings, setPreliminaryBookings] =
    useState<boolean>(false);
  const [bokningsrubriker, setBokningsrubriker] = useState<IBokningsrubrik[]>(
    [] as IBokningsrubrik[]
  );

  const rapport = rapportInfos.BOPDFKalenderReport;
  const userSettings = useReadRapportSettings(rapport.rapportID);
  const doWriteUserSettings = useWriteRapportSettings(rapport.rapportID);
  const [dop, setDop] = useState(true);
  const [lokaluthyrning, setLokalUthyrning] = useState(true);
  const [konfirmation, setKonfirmation] = useState(true);
  const [gudstjänst, setGudstjänst] = useState(true);
  const [ovrigt, setOvrigt] = useState(true);
  const [vigsel, setVigsel] = useState(true);
  const [begravning, setBegravning] = useState(true);
  const [gravsattning, setGravsattning] = useState(true);
  const [snabbokning, setSnabbBokning] = useState(true);
  const [allaBokadeResurser, setVisaAllaBokadeResurser] = useState(true);
  const [statistikStatus, setStatistikStatus] = useState(false);
  const [verksamhetstypGudstjanstyp, setVerksamhetstypGudstjanstyp] =
    useState(false);
  const [externNotering, setExternNotering] = useState(false);
  const [rapportInfoSidfot, setRapportInfoSidfot] = useState(true);
  const [bokningsNummer, setBokningsNummer] = useState(false);
  const [internNotering, setInternNotering] = useState(false);
  const [
    numberOfExistingBookingsWithCurrentResources,
    setNumberOfExistingBookingsWithCurrentResources,
  ] = useState<number | undefined>();
  const [begravningsbyra, setBegravningsbyra] = useState(false);
  const [begravningstyp, setBegravningstyp] = useState(false);
  const [begravningsordning, setBegravningsordning] = useState(false);
  const [gravsattningsTyp, setGravsattningsTyp] = useState(false);
  const [gravplats, setGravplats] = useState(false);

  const sortOptions: string[] = ['Datum', 'Resurs'];
  const { showRapportDialog, urval } = props;
  const dayCount = props.dayCount ?? 7;

  const activeDatabaseOptions = useMemo(
    () => ({
      variables: {
        dbname: activeDatabase ?? 'noDBYet',
      },
      skip: !activeDatabase,
    }),
    [activeDatabase]
  );

  const viewStartDate = useMemo(
    () =>
      dayCount > 1
        ? firstDayInWeek(getDate(props.currentDate))
        : getDate(props.currentDate),
    [dayCount, props.currentDate]
  );

  const viewEndDate = useMemo(
    () => incDay(viewStartDate, dayCount - 1),
    [viewStartDate, dayCount]
  );

  const viewMonthStartDate = useMemo(
    () => firstDayInWeek(firstDayInMonth(props.currentDate)),
    [props.currentDate]
  );

  const viewMonthEndDate = useMemo(
    () => incDay(viewMonthStartDate, WEEK_ROWS * DAY_COLUMNS - 1),
    [viewMonthStartDate]
  );

  const viewDayOverviewStartDate = useMemo(
    () => getDate(props.currentDate),
    [props.currentDate]
  );
  const viewDayOverviewEndDate = useMemo(
    () => incDay(viewDayOverviewStartDate, 1 - 1),
    [viewDayOverviewStartDate]
  );

  const { data: me_data } = useQuery<MeQuery, MeQueryVariables>(
    meQuery,
    activeDatabaseOptions
  );

  const closeDialog = useCallback(() => {
    showRapportDialog(false);
  }, [showRapportDialog]);

  const closeResursDialog = () => {
    setDialogOpen(false);
  };

  const openResursDialog = () => {
    setDialogOpen(true);
  };

  const resursQueryOptions = useMemo(
    () => ({
      variables: {
        dbname: activeDatabaseContext.activeDatabase ?? '',
        routineID: routineID,
      },
      skip: !open || !activeDatabaseContext.activeDatabase,
    }),
    [activeDatabaseContext.activeDatabase, routineID]
  );

  const { data: resurs_data } = useQuery<ResursQuery, ResursQueryVariables>(
    resursQuery,
    resursQueryOptions
  );

  useQuery<FinnsAllaAktiviteter, FinnsAllaAktiviteterVariables>(
    finnsAllaAktiviteter,
    {
      variables: {
        dbname: activeDatabaseContext.activeDatabase ?? '',
        StartDate: fromDate,
        EndDate: toDate,
        Resources: bokningsrubriker.map(item => {
          return item.resursId;
        }),
        Dop: dop,
        Begravning: begravning,
        Gravsattning: gravsattning,
        Gudstjanst: gudstjänst,
        Konfirmation: konfirmation,
        Lokaluthyrning: lokaluthyrning,
        Ovrigt: ovrigt,
        Snabbokning: snabbokning,
        Vigsel: vigsel,
      },
      onCompleted: data => {
        if (
          data?.rtn?.BOFinnsAllaAktiviteter?.antalAktiviteter !== undefined ||
          data?.rtn?.BOFinnsAllaAktiviteter?.antalAktiviteter !== null
        ) {
          setNumberOfExistingBookingsWithCurrentResources(
            data?.rtn?.BOFinnsAllaAktiviteter?.antalAktiviteter
          );
        }
      },
      skip: !bokningsrubriker || !fromDate || !toDate,
    }
  );

  const resources: IBokningsrubrik[] = [];

  if (resurs_data !== undefined) {
    const filteredResources = mergeSort(
      resurs_data?.db?.BOResurs_All ?? [],
      (a, b) => (a.PRIO_ORDNING ?? 1000) - (b.PRIO_ORDNING ?? 1000)
    );
    resources.push(
      ...filteredResources.map(resource => {
        const resRestypNamn =
          resource?._Associations?.ResRestyp?.BO_RESURSTYP_NAMN || '';
        return {
          resursText: `${resource.BO_RESURS_NAMN} (${resRestypNamn})`,
          resursNamn: resource.BO_RESURS_NAMN,
          BO_RESURS_ID: resource.BO_RESURS_ID,
          resursId: resource.id,
          resursTyp: resRestypNamn,
          platsId: resource._Associations?.Forsamling?.id || '',
        };
      })
    );
  }

  const getUniqResourceBaseTypes = (
    resources: ResursQuery_db_BOResurs_All[]
  ) => {
    let resourcesBaseTypes = resources?.map(item => ({
      name: item._Associations?.ResRestyp?.Bastyp,
      value: item.id,
    }));

    const importOrder = ['Plats', 'Person', 'Övrigt'];

    const sortByObject = importOrder.reduce<Record<string, number>>((obj, item, index) => {
      return {
        ...obj,
        [item]: index,
      };
    }, {});

    const customSort = mergeSort(
      resourcesBaseTypes,
      (a, b) => sortByObject[a.name as string] - sortByObject[b.name as string]
    );

    const uniq = uniqWith((a, b) => a.name === b.name, customSort);

    return uniq;
  };

  const GetSubList = (
    resourceData: ResursQuery,
    resTypes: IResursTyp[],
    checkedResources: string[],
    data: string,
    userSign: string | undefined
  ): ITreeSection[] => {
    let subList: ITreeSection[] = [];

    resTypes?.forEach(resType => {
      if (resType.foraldratyp === data) {
        const resSubTypes =
          GetSubList(
            resourceData,
            resTypes,
            checkedResources,
            resType.namn,
            userSign
          ) ?? [];
        const resources =
          resourceData?.db?.BOResurs_All?.reduce<ITreeSection[]>(
            (acc, item2) => {
              if (
                item2._Associations?.ResRestyp.BO_RESURSTYP_NAMN ===
                  resType.namn &&
                item2.Resursnamn &&
                //filters out any resources that the user has already selected
                !checkedResources.includes(item2.id)
              ) {
                acc.push({
                  name: item2.Resursnamn,
                  value: item2.BO_RESURS_ID,
                  id: item2.BO_RESURS_ID,
                  data: bokningsrubrikFromDbResurs(item2),
                });
              }
              return acc;
            },
            []
          ) ?? [];
        //avoid showing empty categories in the tree view
        if (resSubTypes.length > 0 || resources.length > 0) {
          subList.push({
            name: resType.namn,
            value: resType.id,
            subList: [...resSubTypes, ...resources],
            id: resType.id,
          });
        }
      }
    });

    return subList;
  };

  const getSections = useMemo(() => {
    if (resurs_data) {
      // Transform selected resources into array of IDs
      const checkedResources = bokningsrubriker.map(item => item.resursId);
      let resourcesBaseItems = [];
      if (resurs_data?.db?.BOResurs_All) {
        let baseResources = getUniqResourceBaseTypes(
          resurs_data?.db?.BOResurs_All
        );
        // For each base type, build a section of the tree
        resourcesBaseItems = baseResources.map((item: any) => {
          return {
            name: item.name,
            value: item.value,
            id: item.value,
            subList: GetSubList(
              resurs_data,
              resTypes(),
              //array of IDs
              checkedResources,
              item.name,
              me_data?.me?.[0]?.sign
            ),
            open: true,
          };
        });
      } else {
        // Fallback if no resources are found
        resourcesBaseItems = [
          { name: 'Inga resurser hittades', value: '', id: '' },
        ];
      }

      return resourcesBaseItems;
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resurs_data, resTypes, bokningsrubriker, me_data?.me]);

  function getUncheckCurrentUrval(
    ResursTyp: ITreeSection<any>[]
  ): ITreeSection<any>[] {
    for (let i = 0; i < ResursTyp?.length; i++) {
      // eslint-disable-next-line no-prototype-builtins
      if (ResursTyp[i].hasOwnProperty('checked')) {
        if (ResursTyp?.[i].checked && !ResursTyp?.[i].hidden)
          ResursTyp[i].checked = false;
      } else {
        getUncheckCurrentUrval(ResursTyp[i].subList!);
      }
    }
    return ResursTyp;
  }

  function getHiddenCheckedResources(
    ResursTyp: ITreeSection<any>[]
  ): ITreeSection<any>[] {
    for (let i = 0; i < ResursTyp?.length; i++) {
      // eslint-disable-next-line no-prototype-builtins
      if (ResursTyp[i].hasOwnProperty('checked')) {
        if (ResursTyp?.[i].checked) {
          ResursTyp[i].hidden = ResursTyp[i].checked;
        }
      } else if (ResursTyp[i].subList) {
        getHiddenCheckedResources(ResursTyp[i].subList!);
      }
    }
    return ResursTyp;
  }

  const unCheck = () => {
    setSections(getUncheckCurrentUrval(clone(sections)));
  };

  const selectResursDialog = () => {
    // Get all checked resources from the tree
    const newBokningsRubriker = getBokningsrubrikerFromSections(sections);
    // Create a map of existing resources for quick lookup by ID
    const existingResourcesMap = bokningsrubriker.reduce<Record<string, IBokningsrubrik>>((map, item) => {
      map[item.resursId] = item;
      return map;
    }, {});
    // Start with existing selections
    const mergedBokningsRubriker = [...bokningsrubriker];
    // Add new selections if they don't already exist
    newBokningsRubriker.forEach(newItem => {
      if (!existingResourcesMap[newItem.resursId]) {
        mergedBokningsRubriker.push(newItem);
      }
    });
    // Update state and close dialog
    setBokningsrubriker(mergedBokningsRubriker);
    closeResursDialog();
  };

  // Extracts all checked resources from the tree structure
  const getBokningsrubrikerFromSections = (
    sections: ITreeSection<IBokningsrubrik>[]
  ) => {
    // Array to collect all checked resources
    const result: any[] = [];

    //  function to loop the tree
    const extractCheckedItems = (items: any[]) => {
      items.forEach(item => {
        if (item.subList) {
          // Process child nodes if present
          extractCheckedItems(item.subList);
        } else if (item.checked && item.data) {
          // Add to results if this is a checked leaf node
          result.push(item.data);
        }
      });
    };
    // Start loop from the root sections
    extractCheckedItems(sections);
    return result;
  };

  let removeHyphensFromDates = fromDate.replaceAll('-', '');
  let removeHyphensToDates = toDate.replaceAll('-', '');

  let isValidDateFromDates = dateTimeStringIsValidDate(removeHyphensFromDates);
  let isValidDateToDates = dateTimeStringIsValidDate(removeHyphensToDates);

  const notValidDate = (date: string) => {
    if (date) {
      return !isValidDateFromDates;
    } else {
      return isValidDateFromDates;
    }
  };

  const notValidDateSpan = () => {
    if (new Date(fromDate) <= new Date(toDate)) {
      return !isValidDateFromDates;
    } else {
      return isValidDateFromDates;
    }
  };

  const invalidDateMessage = () => {
    if (toDate && !isValidDateToDates) {
      return `Ogiltigt värde "${toDate}" i Tilldatum`;
    } else {
      return undefined;
    }
  };

  const invalidDateSpanMessage = () => {
    if (new Date(fromDate) > new Date(toDate)) {
      return `Tilldatum måste vara senare än Fråndatum`;
    } else {
      return undefined;
    }
  };

  const addSelectedResourcesToBokningsrubriker = (
    urval: IUrval[],
    resources: IBokningsrubrik[]
  ) => {
    const matchedResources: IBokningsrubrik[] = [];
    for (let i = 0; i < resources.length; i++) {
      for (let e = 0; e < urval.length; e++) {
        if (resources[i].BO_RESURS_ID == urval[e].value) {
          matchedResources.push(resources[i]);
        }
      }
    }

    const resourceMap = new Map<string, IBokningsrubrik>();

    matchedResources.forEach(item => {
      resourceMap.set(item.resursId, item);
    });

    return Array.from(resourceMap.values());
  };

  const validateExportToPDF = () => {
    const hasAnyCheckedAktivities: boolean =
      dop ||
      lokaluthyrning ||
      konfirmation ||
      gudstjänst ||
      ovrigt ||
      vigsel ||
      begravning ||
      gravsattning ||
      snabbokning;

    let isValidDateFromDates = dateTimeStringIsValidDate(
      removeHyphensFromDates
    );
    let isValidDateToDates = dateTimeStringIsValidDate(removeHyphensToDates);

    if (
      !hasAnyCheckedAktivities ||
      numberOfExistingBookingsWithCurrentResources === 0 ||
      bokningsrubriker.length == 0 ||
      sorting == '' ||
      toDate == '' ||
      fromDate == '' ||
      !isValidDateFromDates ||
      !isValidDateToDates ||
      notValidDateSpan()
    ) {
      return true;
    } else {
      return false;
    }
  };

  const formatDate = (date: Date | string) => {
    let dateTostr = tryDateToDateString(date);
    const formattedDate =
      dateTostr?.substring(0, 4) +
      '-' +
      dateTostr?.substring(4, 6) +
      '-' +
      dateTostr?.substring(6);

    return formattedDate;
  };

  const removeBokningsrubrikChip = (resursId: string) => {
    return bokningsrubriker.filter(
      (item: IBokningsrubrik) => item.resursId !== resursId
    );
  };

  const exportButtonTooltip = () => {
    let tooltip = [];

    const hasAnyCheckedAktivities: boolean =
      dop ||
      lokaluthyrning ||
      konfirmation ||
      gudstjänst ||
      ovrigt ||
      vigsel ||
      begravning ||
      gravsattning ||
      snabbokning;

    if (!hasAnyCheckedAktivities) {
      tooltip.push('Välj minst en aktivitetstyp ');
    }

    if (bokningsrubriker.length === 0) {
      tooltip.push(' Välj minst en resurs');
    }

    if (
      bokningsrubriker.length !== 0 &&
      numberOfExistingBookingsWithCurrentResources === 0
    ) {
      tooltip.push('Det går inte att skriva ut en tom rapport');
    }

    if (
      toDate == '' ||
      fromDate == '' ||
      !isValidDateFromDates ||
      !isValidDateToDates ||
      notValidDateSpan()
    ) {
      tooltip.push(' Välj ett tidsintervall');
    }

    return tooltip.toString();
  };

  useEffect(() => {
    if (props.currentView === 'Month2' || props.currentView === 'Month') {
      setFromDate(formatDate(viewMonthStartDate));
      setToDate(formatDate(viewMonthEndDate));
    } else if (
      props.currentView === 'DayOverview' ||
      props.currentView === 'Day'
    ) {
      setFromDate(formatDate(viewDayOverviewStartDate));
      setToDate(formatDate(viewDayOverviewEndDate));
    } else {
      setFromDate(formatDate(viewStartDate));
      setToDate(formatDate(viewEndDate));
    }
  }, [
    viewStartDate,
    viewEndDate,
    viewMonthStartDate,
    viewMonthEndDate,
    props.currentView,
    viewDayOverviewStartDate,
    viewDayOverviewEndDate,
  ]);

  useEffect(() => {
    if (bokningsrubriker.length === 0) {
      setBokningsrubriker(
        addSelectedResourcesToBokningsrubriker(urval, resources)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resurs_data]);

  useEffect(() => {
    // Aktiviteter
    setPreliminaryBookings(stringToBoolean(userSettings['preliminara'], false));
    setDop(stringToBoolean(userSettings['dop'], true));
    setLokalUthyrning(stringToBoolean(userSettings['lokaluthyrning'], true));
    setKonfirmation(stringToBoolean(userSettings['konfirmation'], true));
    setGudstjänst(stringToBoolean(userSettings['gudstjanst'], true));
    setOvrigt(stringToBoolean(userSettings['ovrigt'], true));
    setVigsel(stringToBoolean(userSettings['vigsel'], true));
    setBegravning(stringToBoolean(userSettings['begravning'], true));
    setSnabbBokning(stringToBoolean(userSettings['snabbokning'], true));
    setGravsattning(stringToBoolean(userSettings['gravsattning'], true));

    // Inställningar
    setVisaAllaBokadeResurser(
      stringToBoolean(userSettings['allaBokadeResurser'], true)
    );
    setStatistikStatus(stringToBoolean(userSettings['statistikStatus'], false));
    setVerksamhetstypGudstjanstyp(
      stringToBoolean(userSettings['verksamhetstypGudstjanstyp'], false)
    );
    setExternNotering(stringToBoolean(userSettings['externNotering'], false));
    setRapportInfoSidfot(
      stringToBoolean(userSettings['rapportInfoSidfot'], true)
    );
    setBokningsNummer(stringToBoolean(userSettings['bokningsNummer'], false));
    setInternNotering(stringToBoolean(userSettings['internNotering'], false));
    // Inställningar Begravning/Gravsättning
    setBegravningsbyra(stringToBoolean(userSettings['begravningsbyra'], false));
    setBegravningstyp(stringToBoolean(userSettings['begravningstyp'], false));
    setBegravningsordning(
      stringToBoolean(userSettings['begravningsordning'], false)
    );
    setGravplats(stringToBoolean(userSettings['gravplats'], false));
    setGravsattningsTyp(
      stringToBoolean(userSettings['gravsattningsTyp'], false)
    );
  }, [resurs_data, userSettings]);

  const handleDateChange = (value: string) => {
    let filteredValue = value.replace(/[^0-9\-,./]/g, '');
    let numbersCount = value.replace(/[^0-9]/g, '').length;
    if (numbersCount > 8) return;

    if (filteredValue.endsWith('-')) {
      const beforeHyphen = filteredValue.slice(-3, -1);
      if (!/^\d{2}$/.test(beforeHyphen)) {
        filteredValue = filteredValue.slice(0, -1);
      }
    }
    return filteredValue;
  };

  const writeUserSettings = () => {
    const settings: RapportSettings = {
      // Aktiviteter
      preliminara: booleanToString(preliminaryBookings),
      dop: booleanToString(dop),
      lokaluthyrning: booleanToString(lokaluthyrning),
      konfirmation: booleanToString(konfirmation),
      gudstjanst: booleanToString(gudstjänst),
      ovrigt: booleanToString(ovrigt),
      vigsel: booleanToString(vigsel),
      begravning: booleanToString(begravning),
      snabbokning: booleanToString(snabbokning),
      gravsattning: booleanToString(gravsattning),
      // Inställningar
      allaBokadeResurser: booleanToString(allaBokadeResurser),
      statistikStatus: booleanToString(statistikStatus),
      verksamhetstypGudstjanstyp: booleanToString(verksamhetstypGudstjanstyp),
      externNotering: booleanToString(externNotering),
      rapportInfoSidfot: booleanToString(rapportInfoSidfot),
      bokningsNummer: booleanToString(bokningsNummer),
      internNotering: booleanToString(internNotering),
      begravningsbyra: booleanToString(begravningsbyra),
      begravningstyp: booleanToString(begravningstyp),
      begravningsordning: booleanToString(begravningsordning),
      gravplats: booleanToString(gravplats),
      gravsattningsTyp: booleanToString(gravsattningsTyp),
    };
    doWriteUserSettings(settings);
  };

  if (rapport && activeDatabaseContext.activeDatabase) {
    const ExporterKalenderPDFRapport = () => {
      setTimeout(() => {
        props.showRapportDialog(false);
      }, 1000);

      writeUserSettings();
      RapportQuery({
        db: activeDatabaseContext.activeDatabase || '',
        rapportId: rapport.rapportID,
        rapportParams: {
          PreliminaryBookings: preliminaryBookings,
          Frandatum: fromDate,
          Tilldatum: toDate,
          Resources: bokningsrubriker.map(item => {
            return item.resursId;
          }),
          Sortering: sorting,
          Dop: dop,
          Lokaluthyrning: lokaluthyrning,
          Konfirmation: konfirmation,
          Gudstjanst: gudstjänst,
          Ovrigt: ovrigt,
          Vigsel: vigsel,
          Gravsattning: gravsattning,
          Begravning: begravning,
          Snabbokning: snabbokning,
          VisaAllaBokadeResurser: allaBokadeResurser,
          Statistikstatus: statistikStatus,
          VerksamhetstypGudstjanstyp: verksamhetstypGudstjanstyp,
          ExternNotering: externNotering,
          RapportInfoSidFot: rapportInfoSidfot,
          Bokningsnummer: bokningsNummer,
          InternNotering: internNotering,
          Begravningsbyra: begravningsbyra,
          BegravningsTyp: begravningstyp,
          BegravningsOrdning: begravningsordning,
          GravsattningsTyp: gravsattningsTyp,
          Gravplats: gravplats,
        },
      });
    };

    return (
      <EDDialog
        open={props.open}
        disableResponsiveFullScreen
        fullScreenBreakpoint={'sm'}
        title={rapport.benamning}
        subtitle={rapport.typ}
        footerActions={[
          {
            label: 'Stäng',
            onClick: closeDialog,
          },
          {
            label: 'Exportera',
            onClick: ExporterKalenderPDFRapport,
            buttonProps: {
              disabled: validateExportToPDF(),
              disabledReason: exportButtonTooltip(),
            },
          },
        ]}
        onClose={closeDialog}
        classes={{
          paper: classes.paper,
        }}
        footerContent={
          <EDTypography testId="calendar_report_urval_indicator" bold>
            {`Urvalet innehåller ${numberOfExistingBookingsWithCurrentResources} aktiviteter`}
          </EDTypography>
        }
        testId={props.testId}
      >
        {rapport.beskrivning && (
          <EDTypography variant="body2" paragraph>
            {rapport.beskrivning}
          </EDTypography>
        )}
        <EDBox display="flex" flexDirection="column">
          <ObjektVyKort caption="Resurser">
            <EDGrid
              alignItems={'center'}
              container
              spacing={0}
              flexWrap={'nowrap'}
            >
              <EDGrid item>
                <EDIcon
                  iconType="resources_filter"
                  color="inherit"
                  size="medium"
                  marginRight={2}
                />
              </EDGrid>

              <EDGrid item width={'100%'}>
                <EDAutoCompleteEdit<IBokningsrubrik, true>
                  value={bokningsrubriker}
                  multiple
                  options={resources}
                  onChange={values => {
                    setBokningsrubriker(values);
                  }}
                  label="Välj minst en resurs"
                  fullWidth
                  getOptionLabel={option => option.resursText}
                  getOptionSelected={(option, value) =>
                    option.resursId === value.resursId
                  }
                  renderTags={values => {
                    return sortBokningsrubriker(values).map(value => (
                      <EDChip
                        margin={0.5}
                        onDelete={() => {
                          setBokningsrubriker(
                            removeBokningsrubrikChip(value.resursId)
                          );
                        }}
                        key={value.BO_RESURS_ID || ''}
                        label={
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <EDBox
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                lineHeight: '1em',
                                margin: '0px',
                              }}
                            >
                              <div>{value.resursNamn}</div>
                            </EDBox>
                          </div>
                        }
                      />
                    ));
                  }}
                  hideClearIcon
                  hideClear
                  testId="calendar_report_resources"
                />
              </EDGrid>

              <EDGrid item>
                <EDButton
                  padding={1}
                  onClick={openResursDialog}
                  label={isSmallScreen ? '' : `Lägg Till`}
                  resourceBtnStyling={true}
                  icon={<EDIcon iconType="add" color="inherit" />}
                  flat
                  size="small"
                  data-inline="true"
                >
                  <EDDialog
                    fullWidth
                    open={dialogOpen}
                    onClose={closeResursDialog}
                    footerActions={[
                      {
                        label: 'Avmarkera',
                        onClick: unCheck,
                        buttonProps: {
                          disabled:
                            getBokningsrubrikerFromSections(sections).length ===
                            0,
                          disabledReason: 'Inga resurser valda',
                        },
                      },
                      { label: 'Avbryt', onClick: closeResursDialog },
                      { label: 'Använd', onClick: selectResursDialog },
                    ]}
                    title="Välj Resurs"
                    onEnter={() => {
                      // Create a fresh copy of the tree sections
                      const updatedSections = clone(getSections);
                      // Get IDs of all currently selected resources
                      const checkedResources = bokningsrubriker.map(
                        item => item.resursId
                      );
                      //  mark resources as checked
                      const updateCheckedStatus = (sections: any[]) => {
                        sections.forEach(section => {
                          if (section.subList) {
                            updateCheckedStatus(section.subList);
                          } else if (
                            section.data &&
                            checkedResources.includes(section.data.resursId)
                          ) {
                            // Mark as checked if this resource is already selected
                            section.checked = true;
                          }
                        });
                      };
                      // Apply the checked status to all matching resources
                      updateCheckedStatus(updatedSections);
                      // Hide resources that are checked to keep them out of view
                      const sectionsWithHiddenChecked =
                        getHiddenCheckedResources(updatedSections);
                      // Update the tree with the prepared sections
                      setSections(sectionsWithHiddenChecked);
                    }}
                  >
                    <EDTreeSelect
                      sections={sections}
                      searchValue={''}
                      sectionsUpdate={setSections}
                    />
                  </EDDialog>
                </EDButton>
              </EDGrid>
            </EDGrid>
          </ObjektVyKort>

          <EDGrid
            container
            spacing={0}
            justifyContent={'space-between'}
            marginBottom={2}
          >
            <EDGrid item xs={5.9}>
              <ObjektVyKort caption="Datum">
                <EDGrid container>
                  <EDGrid item xs={6}>
                    <EDDatePicker
                      label="Fråndatum"
                      tooltip="Från Datum"
                      required
                      allowOnlyNumbers
                      value={fromDate}
                      onChange={value => {
                        setFromDate(handleDateChange(value) || '');
                      }}
                      helperText={
                        fromDate && !isValidDateFromDates
                          ? `Ogiltigt värde "${fromDate}" i Fråndatum`
                          : undefined
                      }
                      error={notValidDate(fromDate) || notValidDateSpan()}
                      hideClearIcon
                      testId="calendar-report-from-date"
                    />
                  </EDGrid>
                  <EDGrid item xs={6}>
                    <EDDatePicker
                      label="Tilldatum"
                      tooltip="Till Datum"
                      required
                      allowOnlyNumbers
                      value={toDate}
                      onChange={value => {
                        setToDate(handleDateChange(value) || '');
                      }}
                      helperText={
                        invalidDateMessage() || invalidDateSpanMessage()
                      }
                      error={notValidDate(toDate) || notValidDateSpan()}
                      hideClearIcon
                      testId="calendar-report-to-date"
                    />
                  </EDGrid>
                </EDGrid>
              </ObjektVyKort>
            </EDGrid>
            <EDGrid item xs={5.8}>
              <ObjektVyKort caption="Sortering">
                <EDAutoCompleteEdit<string>
                  label="Välj sortering"
                  fullWidth
                  required
                  options={sortOptions}
                  value={sorting || ''}
                  onChange={value => {
                    setSorting(value || '');
                  }}
                />
              </ObjektVyKort>
            </EDGrid>
          </EDGrid>

          <ObjektVyKort caption="Aktiviteter">
            <EDGrid container spacing={0}>
              <EDGrid xs={4}>
                <EDCheckbox
                  label={'Dop'}
                  tooltip={'Dop'}
                  checked={dop}
                  onCheck={() => {
                    setDop(!dop);
                  }}
                  testId="calendar_report_activity_type_dop"
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  label={'Lokaluthyrning'}
                  tooltip={'Lokaluthyrning'}
                  checked={lokaluthyrning}
                  onCheck={() => {
                    setLokalUthyrning(!lokaluthyrning);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  label={'Konfirmation'}
                  tooltip={'Konfirmation'}
                  checked={konfirmation}
                  onCheck={() => {
                    setKonfirmation(!konfirmation);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  label={'Gudstjänst'}
                  tooltip={'Gudstjänst'}
                  checked={gudstjänst}
                  onCheck={() => {
                    setGudstjänst(!gudstjänst);
                  }}
                  testId="calendar_report_activity_type_gudstjanst"
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  label={'Övrigt'}
                  tooltip={'Övrigt'}
                  checked={ovrigt}
                  onCheck={() => {
                    setOvrigt(!ovrigt);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  label={'Vigsel'}
                  tooltip={'Vigsel'}
                  checked={vigsel}
                  onCheck={() => {
                    setVigsel(!vigsel);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  label={'Begravning'}
                  tooltip={'Begravning'}
                  checked={begravning}
                  onCheck={() => {
                    setBegravning(!begravning);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  label={'Gravsättning'}
                  tooltip={'Gravsättning'}
                  checked={gravsattning}
                  onCheck={() => {
                    setGravsattning(!gravsattning);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  label={'Snabbokning'}
                  tooltip={'Snabbokning'}
                  checked={snabbokning}
                  onCheck={() => {
                    setSnabbBokning(!snabbokning);
                  }}
                />
              </EDGrid>
            </EDGrid>
            <EDButton
              size="small"
              onClick={() => {
                setDop(true);
                setLokalUthyrning(true);
                setKonfirmation(true);
                setGudstjänst(true);
                setOvrigt(true);
                setVigsel(true);
                setBegravning(true);
                setGravsattning(true);
                setSnabbBokning(true);
              }}
              flat
            >
              Välj alla
            </EDButton>
            <EDButton
              size="small"
              flat
              onClick={() => {
                setDop(false);
                setLokalUthyrning(false);
                setKonfirmation(false);
                setGudstjänst(false);
                setOvrigt(false);
                setVigsel(false);
                setBegravning(false);
                setGravsattning(false);
                setSnabbBokning(false);
              }}
              testId="clear_all_calendar_report_activities"
            >
              Rensa
            </EDButton>
            <EDGrid>
              <EDCheckbox
                label={'Visa preliminära'}
                tooltip={'Visa preliminära'}
                checked={preliminaryBookings}
                onCheck={() => {
                  setPreliminaryBookings(!preliminaryBookings);
                }}
              />
            </EDGrid>
          </ObjektVyKort>
          <ObjektVyKort caption="Inställningar">
            <EDGrid container spacing={0}>
              <EDGrid xs={4}>
                <EDCheckbox
                  tooltip={'Visa alla bokade resurser'}
                  label={'Visa alla bokade resurser'}
                  checked={allaBokadeResurser}
                  onCheck={() => {
                    setVisaAllaBokadeResurser(!allaBokadeResurser);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  tooltip={'Visa statistikstatus'}
                  label={'Visa statistikstatus'}
                  checked={statistikStatus}
                  onCheck={() => {
                    setStatistikStatus(!statistikStatus);
                  }}
                />
              </EDGrid>
              <EDGrid
                xs={4}
                sx={{
                  display: 'flex',
                }}
              >
                <EDCheckbox
                  tooltip={'Verksamhetstyp/Gudstjänsttyp'}
                  label={'Verksamhetstyp/Gudstjänsttyp'}
                  checked={verksamhetstypGudstjanstyp}
                  onCheck={() => {
                    setVerksamhetstypGudstjanstyp(!verksamhetstypGudstjanstyp);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  tooltip={'Visa extern notering'}
                  label={'Visa extern notering'}
                  checked={externNotering}
                  onCheck={() => {
                    setExternNotering(!externNotering);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  tooltip={'Visa rapportinfo i sidfot'}
                  label={'Visa rapportinfo i sidfot'}
                  checked={rapportInfoSidfot}
                  onCheck={() => {
                    setRapportInfoSidfot(!rapportInfoSidfot);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  tooltip={'Bokningsnummer'}
                  label={'Bokningsnummer'}
                  checked={bokningsNummer}
                  onCheck={() => {
                    setBokningsNummer(!bokningsNummer);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  tooltip={'Visa intern notering'}
                  label={'Visa intern notering'}
                  checked={internNotering}
                  onCheck={() => {
                    setInternNotering(!internNotering);
                  }}
                />
              </EDGrid>
            </EDGrid>
            <EDButton
              size="small"
              onClick={() => {
                setVisaAllaBokadeResurser(true);
                setStatistikStatus(true);
                setVerksamhetstypGudstjanstyp(true);
                setExternNotering(true);
                setRapportInfoSidfot(true);
                setBokningsNummer(true);
                setInternNotering(true);
              }}
              flat
            >
              Välj alla
            </EDButton>
            <EDButton
              size="small"
              flat
              onClick={() => {
                setVisaAllaBokadeResurser(false);
                setStatistikStatus(false);
                setVerksamhetstypGudstjanstyp(false);
                setExternNotering(false);
                setRapportInfoSidfot(false);
                setBokningsNummer(false);
                setInternNotering(false);
              }}
            >
              Rensa
            </EDButton>
            <EDButton
              size="small"
              flat
              onClick={() => {
                setVisaAllaBokadeResurser(true);
                setStatistikStatus(false);
                setVerksamhetstypGudstjanstyp(false);
                setExternNotering(false);
                setRapportInfoSidfot(true);
                setBokningsNummer(false);
                setInternNotering(false);
              }}
            >
              Återställ
            </EDButton>
          </ObjektVyKort>
          <ObjektVyKort caption="Inställningar Begravning/gravsättning">
            <EDGrid container spacing={0}>
              <EDGrid xs={4}>
                <EDCheckbox
                  tooltip={'Visa begravningsbyrå'}
                  label={'Visa begravningsbyrå'}
                  checked={begravningsbyra}
                  onCheck={() => {
                    setBegravningsbyra(!begravningsbyra);
                  }}
                />
                <EDCheckbox
                  tooltip={'Visa begravningstyp'}
                  label={'Visa begravningstyp'}
                  checked={begravningstyp}
                  onCheck={() => {
                    setBegravningstyp(!begravningstyp);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  tooltip={'Visa begravningsordning'}
                  label={'Visa begravningsordning'}
                  checked={begravningsordning}
                  onCheck={() => {
                    setBegravningsordning(!begravningsordning);
                  }}
                />
                <EDCheckbox
                  tooltip={'Visa gravsättning'}
                  label={'Visa gravsättning'}
                  checked={gravsattningsTyp}
                  onCheck={() => {
                    setGravsattningsTyp(!gravsattningsTyp);
                  }}
                />
              </EDGrid>
              <EDGrid xs={4}>
                <EDCheckbox
                  tooltip={'Visa gravplats'}
                  label={'Visa gravplats'}
                  checked={gravplats}
                  onCheck={() => {
                    setGravplats(!gravplats);
                  }}
                />
              </EDGrid>
              <EDButton
                size="small"
                onClick={() => {
                  setBegravningsbyra(true);
                  setBegravningstyp(true);
                  setBegravningsordning(true);
                  setGravsattningsTyp(true);
                  setGravplats(true);
                }}
                flat
              >
                Välj alla
              </EDButton>
              <EDButton
                size="small"
                flat
                onClick={() => {
                  setBegravningsbyra(false);
                  setBegravningstyp(false);
                  setBegravningsordning(false);
                  setGravsattningsTyp(false);
                  setGravplats(false);
                }}
              >
                Rensa
              </EDButton>
              <EDButton size="small" flat onClick={() => {}}>
                Återställ
              </EDButton>
            </EDGrid>
          </ObjektVyKort>
        </EDBox>
      </EDDialog>
    );
  } else return <></>;
};
